import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { formatPercent } from '../../util/format';
import { t as translate, useTranslation } from '../../i18n';

/**
 * StatusCard - Shows open/closed status with visual ratio
 */
const StatusCard = ({ open, closed, title = translate('analytics:reportStatus'), style }) => {
  const { t } = useTranslation('analytics');

  const total = open + closed;
  const openPercent = total > 0 ? (open / total) * 100 : 0;
  const closedPercent = total > 0 ? (closed / total) * 100 : 0;

  return (
    <View style={[styles.container, style]}>
      <Text style={styles.title}>{title}</Text>

      {/* Visual bar */}
      <View style={styles.statusBar}>
        <View style={[styles.openBar, { flex: open || 0.01 }]} />
        <View style={[styles.closedBar, { flex: closed || 0.01 }]} />
      </View>

      {/* Stats row */}
      <View style={styles.statsRow}>
        <View style={styles.statItem}>
          <View style={styles.statHeader}>
            <View style={[styles.dot, { backgroundColor: '#f59e0b' }]} />
            <Text style={styles.statLabel}>{t('active')}</Text>
          </View>
          <Text style={styles.statValue}>{open}</Text>
          <Text style={styles.statPercent}>{formatPercent(openPercent)}</Text>
        </View>

        <View style={styles.divider} />

        <View style={styles.statItem}>
          <View style={styles.statHeader}>
            <View style={[styles.dot, { backgroundColor: '#22c55e' }]} />
            <Text style={styles.statLabel}>{t('closed')}</Text>
          </View>
          <Text style={styles.statValue}>{closed}</Text>
          <Text style={styles.statPercent}>{formatPercent(closedPercent)}</Text>
        </View>
      </View>

      {/* Resolution rate */}
      <View style={styles.resolutionRow}>
        <Feather name="check-circle" size={14} color="#22c55e" />
        <Text style={styles.resolutionText}>
          {t('resolutionRate', { percent: formatPercent(closedPercent, 1) })}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // V3: Card styling matches Submit Report cards
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F0F0F0',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  statusBar: {
    flexDirection: 'row',
    height: 12,
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 16,
  },
  openBar: {
    backgroundColor: '#f59e0b',
  },
  closedBar: {
    backgroundColor: '#22c55e',
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  statLabel: {
    fontSize: 13,
    color: '#6b7280',
  },
  statValue: {
    fontSize: 28,
    fontWeight: '700',
    color: '#111827',
  },
  statPercent: {
    fontSize: 12,
    color: '#9ca3af',
  },
  divider: {
    width: 1,
    height: 50,
    backgroundColor: '#e5e7eb',
  },
  resolutionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  resolutionText: {
    fontSize: 13,
    color: '#22c55e',
    fontWeight: '500',
    marginLeft: 6,
  },
});

export default StatusCard;
